import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Switch, Space, message, ColorPicker, InputNumber, Upload, Card, Tooltip, Image, Slider } from 'antd';
import { PlusOutlined, EditOutlined, UploadOutlined, ReloadOutlined, DownOutlined, UpOutlined, SettingOutlined } from '@ant-design/icons';
import { PostJavamonSeason, PostJavamonRarity, PostJavamonItem } from '../../../dao/jutge/GetJavamonConfig';
import { getBase64, javamonImage } from './javamonUtils';
import { RewardsUsageView } from './RewardsUsageView';

interface TabJavamonsProps {
    isAdmin: boolean;
    loading: boolean;
    config: any;
    setConfig: React.Dispatch<React.SetStateAction<any>>;
    skinsConfig: any;
    rewardsUsage: any;
    loadingRewardsUsage: boolean;
    fetchConfig: () => Promise<void>;
    fetchRewardsUsage: () => Promise<void>;
}

const EVO_COLORS = ['#1890ff', '#f5222d', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#13c2c2', '#faad14', '#2f54eb', '#a0d911'];

export const TabJavamons: React.FC<TabJavamonsProps> = ({
    isAdmin,
    loading,
    config,
    skinsConfig,
    rewardsUsage,
    loadingRewardsUsage,
    fetchConfig,
    fetchRewardsUsage
}) => {
    // Filtros
    const [filterName, setFilterName] = useState<string>('');
    const [filterSeason, setFilterSeason] = useState<number | null>(null);
    const [filterRarity, setFilterRarity] = useState<number | null>(null);
    const [filterTier, setFilterTier] = useState<string | null>(null);
    const [filterActive, setFilterActive] = useState<number | null>(null);

    // Modo de vista ('table' | 'cards' | 'grid' | 'seasons' | 'usage') y drag&drop
    const [javamonView, setJavamonView] = useState<'table' | 'cards' | 'grid' | 'seasons' | 'usage'>('table');
    const [javamonCardSize, setJavamonCardSize] = useState<number>(64);
    const [dragJavamonId, setDragJavamonId] = useState<number | null>(null);
    const [dragOverJavamonId, setDragOverJavamonId] = useState<number | null>(null);
    const [dragOverSeasonId, setDragOverSeasonId] = useState<number | null>(null);
    const [distributionCollapsed, setDistributionCollapsed] = useState<boolean>(true);
    const [savingEvolution, setSavingEvolution] = useState<boolean>(false);

    // Modals
    const [seasonModal, setSeasonModal] = useState(false);
    const [rarityModal, setRarityModal] = useState(false);
    const [rarityManageModal, setRarityManageModal] = useState(false);
    const [togglingSeasonId, setTogglingSeasonId] = useState<number | null>(null);
    const [javamonModal, setJavamonModal] = useState(false);

    // Forms y Edición
    const [form] = Form.useForm();
    const [editingItem, setEditingItem] = useState<any>(null);
    const [fileList, setFileList] = useState<any[]>([]);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    // Líneas de evolución
    const seasonsContainerRef = useRef<HTMLDivElement | null>(null);
    const [evoLines, setEvoLines] = useState<{ x1: number; y1: number; x2: number; y2: number; color: string }[]>([]);

    const parseHabilitatIds = (val: any): number[] => {
        if (!val) return [];
        if (Array.isArray(val)) return val.map((x: any) => Number(x)).filter((n: number) => !isNaN(n));
        return String(val).split(",").map(s => Number(s.trim())).filter(n => !isNaN(n));
    };

    const computeEvoLines = useCallback(() => {
        const container = seasonsContainerRef.current;
        if (!container) { setEvoLines([]); return; }
        const containerRect = container.getBoundingClientRect();
        const lines: { x1: number; y1: number; x2: number; y2: number; color: string }[] = [];

        const byId: Record<number, any> = {};
        config.javamons.forEach((j: any) => { byId[j.id] = j; });
        const isTarget: Record<number, boolean> = {};
        config.javamons.forEach((j: any) => { if (j.evoluciona_a) isTarget[j.evoluciona_a] = true; });

        const colorOfId: Record<number, string> = {};
        let colorIdx = 0;
        config.javamons.forEach((j: any) => {
            if (isTarget[j.id]) return;
            if (!j.evoluciona_a) return;
            const color = EVO_COLORS[colorIdx % EVO_COLORS.length];
            colorIdx++;
            let current: any = j;
            const guard = new Set<number>();
            while (current && !guard.has(current.id)) {
                guard.add(current.id);
                colorOfId[current.id] = color;
                current = current.evoluciona_a ? byId[current.evoluciona_a] : null;
            }
        });

        config.javamons.forEach((j: any) => {
            if (!j.evoluciona_a) return;
            const fromEl = container.querySelector(`[data-jm-id="${j.id}"]`) as HTMLElement | null;
            const toEl = container.querySelector(`[data-jm-id="${j.evoluciona_a}"]`) as HTMLElement | null;
            if (!fromEl || !toEl) return;
            const a = fromEl.getBoundingClientRect();
            const b = toEl.getBoundingClientRect();
            lines.push({
                x1: a.left + a.width / 2 - containerRect.left,
                y1: a.top + a.height / 2 - containerRect.top,
                x2: b.left + b.width / 2 - containerRect.left,
                y2: b.top + b.height / 2 - containerRect.top,
                color: colorOfId[j.id] || '#1890ff'
            });
        });
        setEvoLines(lines);
    }, [config.javamons]);

    useLayoutEffect(() => {
        if (javamonView !== 'seasons') return;
        const t = setTimeout(computeEvoLines, 0);
        return () => clearTimeout(t);
    }, [javamonView, config.javamons, filterName, filterSeason, filterRarity, filterTier, filterActive, javamonCardSize, computeEvoLines]);

    useEffect(() => {
        if (javamonView !== 'seasons') return;
        const handler = () => computeEvoLines();
        window.addEventListener('resize', handler);
        window.addEventListener('scroll', handler, true);
        return () => {
            window.removeEventListener('resize', handler);
            window.removeEventListener('scroll', handler, true);
        };
    }, [javamonView, computeEvoLines]);

    const handleSaveSeason = async (values: any) => {
        try {
            const body = { ...values, id: editingItem?.id || 0, active: values.active ? 1 : 0 };
            const res = await PostJavamonSeason(body);
            if (!res) throw new Error('Error');
            message.success("Temporada guardada");
            setSeasonModal(false);
            fetchConfig();
        } catch (e) {
            message.error("Error al guardar");
        }
    };

    const handleToggleSeasonActive = async (season: any, checked: boolean) => {
        setTogglingSeasonId(season.id);
        try {
            const body = {
                ...season,
                id: season.id,
                active: checked ? 1 : 0
            };
            const res = await PostJavamonSeason(body);
            if (!res) throw new Error('Error');
            message.success(`Temporada "${season.nom}" ${checked ? 'activada' : 'deshabilitada'}`);
            fetchConfig();
        } catch (e) {
            message.error("Error al cambiar el estado de la temporada");
        } finally {
            setTogglingSeasonId(null);
        }
    };

    const handleSaveRarity = async (values: any) => {
        try {
            const body = {
                ...values,
                id: editingItem?.id || 0,
                active: values.active ? 1 : 0,
                color: typeof values.color === 'string' ? values.color : values.color.toHexString()
            };
            const res = await PostJavamonRarity(body);
            if (!res) throw new Error('Error');
            message.success("Rareza guardada");
            setRarityModal(false);
            fetchConfig();
        } catch (e) {
            message.error("Error al guardar");
        }
    };

    const handleSaveJavamon = async (values: any) => {
        try {
            const formData = new FormData();
            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('file', fileList[0].originFileObj);
            }

            const season = config.seasons.find((s: any) => s.id === values.id_season);
            const rarity = config.rarities.find((r: any) => r.id === values.id_rarity);

            const body: any = {
                ...values,
                id: editingItem?.id || 0,
                active: values.active ? 1 : 0,
                season_name: season?.nom,
                rarity_name: rarity?.nom
            };

            if (fileList.length > 0 && fileList[0].originFileObj) {
                const b64 = await getBase64(fileList[0].originFileObj);
                body.imagen = b64;
            }

            formData.append('json', JSON.stringify(body));

            const res = await PostJavamonItem(formData);
            if (!res) throw new Error('Error');
            message.success("Javamon guardado");
            setJavamonModal(false);
            setFileList([]);
            setPreviewImage(null);
            fetchConfig();
        } catch (e) {
            message.error("Error al guardar");
        }
    };

    const openJavamonEdit = (record: any) => {
        setEditingItem(record);
        form.setFieldsValue({ ...record, habilitat_ids: parseHabilitatIds(record.habilitat_ids) });
        setJavamonModal(true);
        setFileList([]);
        setPreviewImage(null);
    };

    const handleSetSeason = async (source: any, seasonId: number) => {
        if (!source || source.id_season === seasonId) return;
        setSavingEvolution(true);
        try {
            const season = config.seasons.find((s: any) => s.id === seasonId);
            const rarity = config.rarities.find((r: any) => r.id === source.id_rarity);

            const body: any = {
                id: source.id,
                nom: source.nom,
                id_rarity: source.id_rarity,
                id_season: seasonId,
                image: source.image,
                active: source.active ? 1 : 0,
                tier: source.tier,
                descr: source.descr,
                chance: source.chance,
                vida: source.vida,
                defensa: source.defensa,
                velocidad: source.velocidad,
                ataque: source.ataque,
                evoluciona_a: source.evoluciona_a,
                season_name: season?.nom,
                rarity_name: rarity?.nom
            };

            const formData = new FormData();
            formData.append('json', JSON.stringify(body));

            const res = await PostJavamonItem(formData);
            if (!res) throw new Error('Error');
            message.success(`${source.nom} movido a ${season?.nom || 'temporada ' + seasonId}`);
            fetchConfig();
        } catch (e) {
            message.error("Error al reasignar temporada");
        } finally {
            setSavingEvolution(false);
        }
    };

    const handleSetEvolution = async (source: any, targetId: number | null) => {
        if (!source) return;
        if (source.id === targetId) {
            message.warning("Un Javamon no puede evolucionar a sí mismo");
            return;
        }
        setSavingEvolution(true);
        try {
            const season = config.seasons.find((s: any) => s.id === source.id_season);
            const rarity = config.rarities.find((r: any) => r.id === source.id_rarity);

            const body: any = {
                id: source.id,
                nom: source.nom,
                id_rarity: source.id_rarity,
                id_season: source.id_season,
                image: source.image,
                active: source.active ? 1 : 0,
                tier: source.tier,
                descr: source.descr,
                chance: source.chance,
                vida: source.vida,
                defensa: source.defensa,
                velocidad: source.velocidad,
                ataque: source.ataque,
                evoluciona_a: targetId,
                season_name: season?.nom,
                rarity_name: rarity?.nom
            };

            const formData = new FormData();
            formData.append('json', JSON.stringify(body));

            const res = await PostJavamonItem(formData);
            if (!res) throw new Error('Error');

            if (targetId) {
                const target = config.javamons.find((j: any) => j.id === targetId);
                message.success(`${source.nom} evoluciona a ${target?.nom || '#' + targetId}`);
            } else {
                message.success(`${source.nom} ya no evoluciona`);
            }
            fetchConfig();
        } catch (e) {
            message.error("Error al asignar la evolución");
        } finally {
            setSavingEvolution(false);
        }
    };

    const getFilteredJavamons = () => config.javamons.filter((j: any) => {
        if (filterName && !j.nom.toLowerCase().includes(filterName.toLowerCase())) return false;
        if (filterSeason !== null && j.id_season !== filterSeason) return false;
        if (filterRarity !== null && j.id_rarity !== filterRarity) return false;
        if (filterTier && j.tier !== filterTier) return false;
        if (filterActive !== null && j.active !== filterActive) return false;
        return true;
    });

    const renderJavamonCard = (j: any, options: { showRemove?: boolean; enableEvolutionDrop?: boolean; size?: number } = {}) => {
        const enableEvolutionDrop = options.enableEvolutionDrop !== false;
        const rarity = config.rarities.find((r: any) => r.id === j.id_rarity);
        const isDragging = dragJavamonId === j.id;
        const isDragOver = enableEvolutionDrop && dragOverJavamonId === j.id && dragJavamonId !== j.id;
        const imgSize = options.size ?? 64;
        const cardWidth = imgSize + 28;
        return (
            <div
                key={j.id}
                data-jm-id={j.id}
                draggable={isAdmin && !savingEvolution}
                onDragStart={(e) => { e.stopPropagation(); setDragJavamonId(j.id); }}
                onDragEnd={() => { setDragJavamonId(null); setDragOverJavamonId(null); }}
                onDragOver={enableEvolutionDrop ? (e) => { e.preventDefault(); if (dragOverJavamonId !== j.id) setDragOverJavamonId(j.id); } : undefined}
                onDragLeave={enableEvolutionDrop ? () => { if (dragOverJavamonId === j.id) setDragOverJavamonId(null); } : undefined}
                onDrop={enableEvolutionDrop ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverJavamonId(null);
                    if (dragJavamonId != null && dragJavamonId !== j.id) {
                        const source = config.javamons.find((x: any) => x.id === dragJavamonId);
                        handleSetEvolution(source, j.id);
                    }
                    setDragJavamonId(null);
                } : undefined}
                title={isAdmin ? (enableEvolutionDrop ? `Arrastra otro Javamon aquí para que evolucione a ${j.nom}` : j.nom) : j.nom}
                style={{
                    width: cardWidth,
                    padding: 6,
                    borderRadius: 10,
                    border: `2px solid ${isDragOver ? '#1890ff' : (rarity?.color || '#e0e0e0')}`,
                    background: isDragOver ? '#e6f7ff' : '#fff',
                    boxShadow: isDragging ? '0 0 0 2px #1890ff' : '0 2px 6px rgba(0,0,0,0.08)',
                    opacity: isDragging ? 0.5 : (j.active ? 1 : 0.55),
                    cursor: isAdmin ? 'grab' : 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    position: 'relative',
                    transition: 'all 0.15s'
                }}
            >
                {options.showRemove && isAdmin && (
                    <Tooltip title="Quitar evolución">
                        <span
                            onClick={(e) => { e.stopPropagation(); handleSetEvolution(j, null); }}
                            style={{
                                position: 'absolute', top: -8, right: -8, width: 20, height: 20,
                                borderRadius: '50%', background: '#ff4d4f', color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, cursor: 'pointer', zIndex: 2, lineHeight: 1
                            }}
                        >×</span>
                    </Tooltip>
                )}
                <div
                    onClick={(e) => { e.stopPropagation(); if (isAdmin) openJavamonEdit(j); }}
                    title={isAdmin ? `Editar ${j.nom}` : j.nom}
                    style={{ cursor: isAdmin ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <img
                        src={javamonImage(j)}
                        alt={j.nom}
                        width={imgSize}
                        height={imgSize}
                        style={{ objectFit: 'contain', pointerEvents: 'none' }}
                    />
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, textAlign: 'center', lineHeight: 1.1, wordBreak: 'break-word' }}>
                    {j.nom}
                </div>
                {j.tier && (
                    <Tag color={j.tier === 'S' ? 'gold' : j.tier === 'A' ? 'purple' : j.tier === 'B' ? 'blue' : 'default'} style={{ margin: 0, fontSize: 9, padding: '0 4px', lineHeight: '14px' }}>
                        {j.tier}
                    </Tag>
                )}
            </div>
        );
    };

    const renderJavamonGrid = () => {
        const filtered = getFilteredJavamons();
        const byId: Record<number, any> = {};
        config.javamons.forEach((j: any) => { byId[j.id] = j; });

        const isTarget: Record<number, boolean> = {};
        config.javamons.forEach((j: any) => {
            if (j.evoluciona_a) isTarget[j.evoluciona_a] = true;
        });

        const chains: any[][] = [];
        const usedInChain = new Set<number>();
        config.javamons.forEach((j: any) => {
            const hasEvo = !!j.evoluciona_a;
            const isRoot = !isTarget[j.id];
            if (isRoot && hasEvo) {
                const chain: any[] = [];
                let current: any = j;
                const guard = new Set<number>();
                while (current && !guard.has(current.id)) {
                    guard.add(current.id);
                    chain.push(current);
                    usedInChain.add(current.id);
                    current = current.evoluciona_a ? byId[current.evoluciona_a] : null;
                }
                if (chain.length > 1) chains.push(chain);
            }
        });

        const singles = filtered.filter((j: any) => !usedInChain.has(j.id));
        const filteredIds = new Set(filtered.map((j: any) => j.id));
        const visibleChains = chains.filter(chain => chain.some(j => filteredIds.has(j.id)));

        return (
            <div>
                <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 8, fontSize: 13 }}>
                    🧬 <b>Evoluciones:</b> arrastra un Javamon <b>encima de otro</b> para que el primero evolucione en el segundo.
                    Pulsa la <b style={{ color: '#ff4d4f' }}>×</b> de una tarjeta en una cadena para quitar su evolución.
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, maxWidth: 320 }}>
                    <span style={{ fontSize: 13, color: '#8c8c8c', whiteSpace: 'nowrap' }}>Tamaño:</span>
                    <Slider min={40} max={160} step={4} value={javamonCardSize} onChange={(v) => setJavamonCardSize(v as number)} style={{ flex: 1 }} />
                </div>

                {visibleChains.length > 0 && (
                    <Card size="small" title={`Cadenas de evolución (${visibleChains.length})`} style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {visibleChains.map((chain, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {chain.map((j: any, i: number) => (
                                        <React.Fragment key={j.id}>
                                            {renderJavamonCard(j, { showRemove: !!j.evoluciona_a, size: javamonCardSize })}
                                            {i < chain.length - 1 && (
                                                <span style={{ fontSize: 22, color: '#1890ff', fontWeight: 'bold' }}>→</span>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                <Card size="small" title={`Todos los Javamons (${singles.length})`}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {singles.length === 0
                            ? <span style={{ color: '#aaa' }}>No hay Javamons sueltos con los filtros actuales.</span>
                            : singles.map((j: any) => renderJavamonCard(j, { size: javamonCardSize }))}
                    </div>
                </Card>
            </div>
        );
    };

    const renderJavamonCardsGrid = () => {
        const filtered = getFilteredJavamons();
        return (
            <Card size="small" title={`${filtered.length} Javamons`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, maxWidth: 320 }}>
                    <span style={{ fontSize: 13, color: '#8c8c8c', whiteSpace: 'nowrap' }}>Tamaño:</span>
                    <Slider
                        min={40}
                        max={160}
                        step={4}
                        value={javamonCardSize}
                        onChange={(v) => setJavamonCardSize(v as number)}
                        style={{ flex: 1 }}
                    />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {filtered.length === 0
                        ? <span style={{ color: '#aaa' }}>No hay Javamons con los filtros actuales.</span>
                        : filtered.map((j: any) => renderJavamonCard(j, { enableEvolutionDrop: false, size: javamonCardSize }))}
                </div>
            </Card>
        );
    };

    const renderSeasonsGrid = () => {
        const filtered = getFilteredJavamons();
        const columns: { id: number | null; nom: string }[] = [
            ...config.seasons.map((s: any) => ({ id: s.id, nom: s.nom })),
        ];
        const hasOrphans = filtered.some((j: any) => !config.seasons.find((s: any) => s.id === j.id_season));
        if (hasOrphans) columns.push({ id: null, nom: 'Sin temporada' });

        return (
            <div>
                <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, fontSize: 13 }}>
                    📅 <b>Temporadas:</b> arrastra un Javamon a la columna de otra temporada para reasignarlo.
                    Las líneas azules indican las evoluciones entre Javamons.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, maxWidth: 320 }}>
                    <span style={{ fontSize: 13, color: '#8c8c8c', whiteSpace: 'nowrap' }}>Tamaño:</span>
                    <Slider min={40} max={160} step={4} value={javamonCardSize} onChange={(v) => setJavamonCardSize(v as number)} style={{ flex: 1 }} />
                </div>
                <div ref={seasonsContainerRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <svg
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10, overflow: 'visible' }}
                    >
                        <defs>
                            {EVO_COLORS.map((c, idx) => (
                                <marker key={idx} id={`evoArrow-${idx}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                                    <path d="M0,0 L6,3 L0,6 Z" fill={c} />
                                </marker>
                            ))}
                        </defs>
                        {evoLines.map((l, i) => {
                            const colorIdx = EVO_COLORS.indexOf(l.color);
                            const markerId = colorIdx >= 0 ? `evoArrow-${colorIdx}` : 'evoArrow-0';
                            return (
                                <line
                                    key={i}
                                    x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                                    stroke={l.color} strokeWidth={2.5} strokeOpacity={0.85}
                                    strokeDasharray="5,4"
                                    markerEnd={`url(#${markerId})`}
                                />
                            );
                        })}
                    </svg>
                    {columns.map((col) => {
                        const jms = filtered.filter((j: any) => (col.id === null ? !config.seasons.find((s: any) => s.id === j.id_season) : j.id_season === col.id));
                        const isOver = dragOverSeasonId === col.id;
                        return (
                            <div
                                key={col.id ?? 'none'}
                                onDragOver={(e) => { e.preventDefault(); if (col.id !== null && dragOverSeasonId !== col.id) setDragOverSeasonId(col.id); }}
                                onDragLeave={() => { if (dragOverSeasonId === col.id) setDragOverSeasonId(null); }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragOverSeasonId(null);
                                    if (col.id !== null && dragJavamonId != null) {
                                        const source = config.javamons.find((x: any) => x.id === dragJavamonId);
                                        handleSetSeason(source, col.id);
                                    }
                                    setDragJavamonId(null);
                                }}
                                style={{
                                    width: '100%',
                                    background: isOver ? '#e6f7ff' : '#fafafa',
                                    border: `2px ${isOver ? 'dashed #1890ff' : 'solid #e0e0e0'}`,
                                    borderRadius: 10,
                                    padding: 12
                                }}
                            >
                                <div style={{ fontWeight: 'bold', marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{col.nom}</span>
                                    <Tag>{jms.length}</Tag>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, minHeight: 90 }}>
                                    {jms.length === 0
                                        ? <span style={{ color: '#bbb', fontSize: 12 }}>Suelta Javamons aquí</span>
                                        : jms.map((j: any) => renderJavamonCard(j, { enableEvolutionDrop: false, size: javamonCardSize }))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const columnsRarity = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Nombre', dataIndex: 'nom', key: 'nom' },
        { title: 'Color', dataIndex: 'color', key: 'color', render: (val: string) => <Tag color={val}>{val}</Tag> },
        { title: '% Aparición', dataIndex: 'chance', key: 'chance', render: (val: number) => <b>{val}%</b> },
        { title: 'Activa', dataIndex: 'active', key: 'active', render: (val: number) => <Tag color={val ? 'green' : 'red'}>{val ? 'SÍ' : 'NO'}</Tag> },
        ...(isAdmin ? [{
            title: 'Acciones',
            key: 'actions',
            render: (_: any, record: any) => (
                <Button icon={<EditOutlined />} onClick={() => { setEditingItem(record); form.setFieldsValue(record); setRarityModal(true); }} />
            )
        }] : [])
    ];

    const columnsJavamon = [
        {
            title: 'Imagen',
            dataIndex: 'imagen',
            key: 'imagen',
            width: 75,
            fixed: 'left' as const,
            align: 'center' as const,
            render: (_: any, record: any) => <Image src={javamonImage(record)} alt="img" width={40} height={40} style={{ objectFit: 'contain' }} />
        },
        {
            title: 'Nombre',
            dataIndex: 'nom',
            key: 'nom',
            width: 140,
            fixed: 'left' as const,
            render: (v: string) => <b>{v}</b>
        },
        {
            title: 'Rareza',
            dataIndex: 'id_rarity',
            key: 'id_rarity',
            width: 110,
            render: (id: number) => {
                const r = config.rarities.find((r: any) => r.id === id);
                return <Tag color={r?.color} style={{ margin: 0 }}>{r?.nom || 'Desconocida'}</Tag>;
            }
        },
        {
            title: 'Temporada',
            dataIndex: 'id_season',
            key: 'id_season',
            width: 110,
            render: (id: number) => {
                const s = config.seasons.find((s: any) => s.id === id);
                return <Tag style={{ margin: 0 }}>{s?.nom || 'Ninguna'}</Tag>;
            }
        },
        {
            title: 'Tier',
            dataIndex: 'tier',
            key: 'tier',
            width: 70,
            align: 'center' as const,
            render: (t: string) => <Tag color={t === 'S' ? 'gold' : t === 'A' ? 'purple' : t === 'B' ? 'blue' : 'default'} style={{ margin: 0 }}>{t || '-'}</Tag>
        },
        {
            title: '% en Rareza',
            dataIndex: 'chance',
            key: 'chance',
            width: 95,
            align: 'center' as const,
            render: (val: number) => <span>{val || 0}%</span>
        },
        {
            title: 'Stats (V / D / Vel / Atq)',
            key: 'stats',
            width: 180,
            render: (_: any, record: any) => (
                <Space direction="horizontal" size={3}>
                    <Tooltip title="Vida"><Tag color="red" style={{ margin: 0, padding: '0 4px', fontSize: 11 }}>❤️ {record.vida || 0}</Tag></Tooltip>
                    <Tooltip title="Defensa"><Tag color="blue" style={{ margin: 0, padding: '0 4px', fontSize: 11 }}>🛡️ {record.defensa || 0}</Tag></Tooltip>
                    <Tooltip title="Velocidad"><Tag color="green" style={{ margin: 0, padding: '0 4px', fontSize: 11 }}>⚡ {record.velocidad || 0}</Tag></Tooltip>
                    <Tooltip title="Ataque"><Tag color="orange" style={{ margin: 0, padding: '0 4px', fontSize: 11 }}>⚔️ {record.ataque || 0}</Tag></Tooltip>
                </Space>
            )
        },
        {
            title: 'Magies (combat)',
            key: 'habilitat_ids',
            width: 160,
            render: (_: any, record: any) => {
                const ids = parseHabilitatIds(record.habilitat_ids);
                if (!ids.length) return <span style={{ color: '#aaa', fontSize: 12 }}>Cap</span>;
                return (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {ids.map((id: number) => {
                            const m = (skinsConfig.magias || []).find((x: any) => x.id === id);
                            return (
                                <Tag key={id} color="magenta" style={{ margin: 0, fontSize: 11 }}>
                                    {m ? m.nom : `#${id}`}
                                </Tag>
                            );
                        })}
                    </div>
                );
            }
        },
        {
            title: 'Habilidades (Descr)',
            dataIndex: 'descr',
            key: 'descr',
            width: 170,
            render: (descr: string) => {
                if (!descr) return <span style={{ color: '#aaa', fontSize: 12 }}>Ninguna</span>;
                return (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {descr.includes('PROBLEMA') && <Tag color="cyan" style={{ margin: 0, fontSize: 10 }}>PROBLEMA</Tag>}
                        {descr.includes('TIEMPO') && <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>TIEMPO</Tag>}
                        {descr.includes('NOTA') && <Tag color="gold" style={{ margin: 0, fontSize: 10 }}>NOTA</Tag>}
                        {descr.includes('CANDADO') && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>CANDADO</Tag>}
                        {descr.includes('TEST') && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>TESTS</Tag>}
                        {!descr.match(/PROBLEMA|TIEMPO|NOTA|CANDADO|TEST/) && <span style={{ fontSize: 12 }}>{descr}</span>}
                    </div>
                );
            }
        },
        {
            title: 'Evoluciona a',
            dataIndex: 'evoluciona_a',
            key: 'evoluciona_a',
            width: 120,
            render: (id: number) => {
                if (!id) return <span style={{ color: '#ccc', fontSize: 12 }}>No evoluciona</span>;
                const target = config.javamons.find((j: any) => j.id === id);
                return <Tag color="geekblue" style={{ margin: 0, fontSize: 11 }}>{target?.nom || `#${id}`}</Tag>;
            }
        },
        {
            title: 'Activo',
            dataIndex: 'active',
            key: 'active',
            width: 80,
            align: 'center' as const,
            render: (val: number) => <Tag color={val ? 'green' : 'red'} style={{ margin: 0 }}>{val ? 'SÍ' : 'NO'}</Tag>
        },
        ...(isAdmin ? [{
            title: 'Acciones',
            key: 'actions',
            width: 80,
            fixed: 'right' as const,
            align: 'center' as const,
            render: (_: any, record: any) => (
                <Button icon={<EditOutlined />} size="small" onClick={() => openJavamonEdit(record)} />
            )
        }] : [])
    ];

    // Cálculos globales de conformidad
    let totalCombinations = 0;
    let completedCombinations = 0;
    config.seasons.forEach((s: any) => {
        config.rarities.forEach((r: any) => {
            totalCombinations++;
            const sum = config.javamons
                .filter((j: any) => j.id_season === s.id && j.id_rarity === r.id)
                .reduce((acc: number, j: any) => acc + (j.chance || 0), 0);
            if (sum === 100) completedCombinations++;
        });
    });

    return (
        <div>
            {/* Cabecera / Distribución */}
            <div style={{
                marginBottom: 20,
                background: '#ffffff',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                overflow: 'hidden'
            }}>
                <div
                    onClick={() => setDistributionCollapsed(!distributionCollapsed)}
                    style={{
                        padding: '14px 20px',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        borderBottom: distributionCollapsed ? 'none' : '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 18 }}>📊</span>
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
                            Distribución de Probabilidad de Aparición
                        </span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                            (Gestión de temporadas y rarezas · Cada rareza por temporada debe sumar 100%)
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                        <Tag
                            color={completedCombinations === totalCombinations ? 'success' : 'warning'}
                            style={{ margin: 0, fontWeight: 600, padding: '3px 10px', borderRadius: 12 }}
                        >
                            {completedCombinations === totalCombinations
                                ? '✓ Todas al 100%'
                                : `⚠️ ${completedCombinations} de ${totalCombinations} correctas`}
                        </Tag>
                        {isAdmin && (
                            <Button
                                type="primary"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => { setEditingItem(null); form.resetFields(); form.setFieldsValue({ active: 1 }); setSeasonModal(true); }}
                                style={{ borderRadius: 6, fontWeight: 600 }}
                            >
                                Nueva Temporada
                            </Button>
                        )}
                        <Button
                            size="small"
                            icon={<SettingOutlined />}
                            onClick={() => setRarityManageModal(true)}
                            style={{ borderRadius: 6 }}
                        >
                            Gestionar Rarezas
                        </Button>
                        <Button
                            size="small"
                            type="text"
                            icon={distributionCollapsed ? <DownOutlined /> : <UpOutlined />}
                            onClick={() => setDistributionCollapsed(!distributionCollapsed)}
                            style={{ fontSize: 12, color: '#64748b' }}
                        >
                            {distributionCollapsed ? 'Mostrar detalle' : 'Minimizar'}
                        </Button>
                    </div>
                </div>

                {!distributionCollapsed && (
                    <div style={{
                        padding: '16px 20px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
                        gap: 14,
                        background: '#f8fafc'
                    }}>
                        {config.seasons.map((s: any) => {
                            const rarityStats = config.rarities.map((r: any) => {
                                const sum = config.javamons
                                    .filter((j: any) => j.id_season === s.id && j.id_rarity === r.id)
                                    .reduce((acc: number, j: any) => acc + (j.chance || 0), 0);
                                return { rarity: r, sum, isOk: sum === 100 };
                            });
                            const allOkInSeason = rarityStats.every((rs: any) => rs.isOk);
                            const isActive = s.active === 1 || s.active === true;

                            return (
                                <div
                                    key={s.id}
                                    style={{
                                        background: '#ffffff',
                                        borderRadius: 10,
                                        border: `1px solid ${isActive ? (allOkInSeason ? '#bbf7d0' : '#fed7aa') : '#e2e8f0'}`,
                                        padding: '12px 14px',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 10,
                                        opacity: isActive ? 1 : 0.65,
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                            <span
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    color: '#0f172a',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                title={s.nom}
                                            >
                                                {s.nom}
                                            </span>
                                            {s.descr && (
                                                <Tooltip title={s.descr}>
                                                    <span style={{ color: '#94a3b8', fontSize: 12, cursor: 'help' }}>ℹ️</span>
                                                </Tooltip>
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                            {allOkInSeason ? (
                                                <Tag color="success" style={{ margin: 0, fontSize: 11, padding: '0 6px', borderRadius: 10 }}>
                                                    ✓ 100% OK
                                                </Tag>
                                            ) : (
                                                <Tag color="warning" style={{ margin: 0, fontSize: 11, padding: '0 6px', borderRadius: 10 }}>
                                                    Incompleta
                                                </Tag>
                                            )}

                                            {isAdmin && (
                                                <>
                                                    <Tooltip title={isActive ? "Deshabilitar temporada" : "Habilitar temporada"}>
                                                        <Switch
                                                            size="small"
                                                            checked={isActive}
                                                            loading={togglingSeasonId === s.id}
                                                            onChange={(checked) => handleToggleSeasonActive(s, checked)}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title="Editar temporada">
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            icon={<EditOutlined style={{ color: '#1677ff' }} />}
                                                            onClick={() => {
                                                                setEditingItem(s);
                                                                form.setFieldsValue({ ...s, active: isActive ? 1 : 0 });
                                                                setSeasonModal(true);
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {rarityStats.map(({ rarity, sum, isOk }: any) => {
                                            const statusColor = isOk ? '#22c55e' : (sum > 100 ? '#ef4444' : '#f59e0b');
                                            const statusBg = isOk ? '#f0fdf4' : (sum > 100 ? '#fef2f2' : '#fffbeb');
                                            const statusBorder = isOk ? '#dcfce7' : (sum > 100 ? '#fee2e2' : '#fef3c7');

                                            return (
                                                <div
                                                    key={`${s.id}-${rarity.id}`}
                                                    style={{
                                                        background: statusBg,
                                                        border: `1px solid ${statusBorder}`,
                                                        borderRadius: 6,
                                                        padding: '5px 8px',
                                                        fontSize: 12
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                                        <span style={{ color: '#475569', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: rarity.color || '#94a3b8', display: 'inline-block' }} />
                                                            {rarity.nom}
                                                        </span>
                                                        <span style={{
                                                            fontWeight: 700,
                                                            color: statusColor,
                                                            fontSize: 12
                                                        }}>
                                                            {sum}% {isOk ? '✓' : ''}
                                                        </span>
                                                    </div>
                                                    <div style={{
                                                        width: '100%',
                                                        height: 5,
                                                        background: '#e2e8f0',
                                                        borderRadius: 3,
                                                        overflow: 'hidden'
                                                    }}>
                                                        <div style={{
                                                            width: `${Math.min(sum, 100)}%`,
                                                            height: '100%',
                                                            background: statusColor,
                                                            transition: 'width 0.3s'
                                                        }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Filtros de búsqueda */}
            <div style={{ marginBottom: 16, padding: '16px', background: '#fafafa', border: '1px solid #eee', borderRadius: '8px' }}>
                <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Filtros de búsqueda:</div>
                <Space wrap>
                    <Input
                        placeholder="Buscar por nombre..."
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Select
                        placeholder="Temporada"
                        style={{ width: 150 }}
                        value={filterSeason}
                        onChange={(v) => setFilterSeason(v)}
                        allowClear
                    >
                        {config.seasons.map((s: any) => <Select.Option key={s.id} value={s.id}>{s.nom}</Select.Option>)}
                    </Select>
                    <Select
                        placeholder="Rareza"
                        style={{ width: 150 }}
                        value={filterRarity}
                        onChange={(v) => setFilterRarity(v)}
                        allowClear
                    >
                        {config.rarities.map((r: any) => <Select.Option key={r.id} value={r.id}>{r.nom}</Select.Option>)}
                    </Select>
                    <Select
                        placeholder="Tier"
                        style={{ width: 100 }}
                        value={filterTier}
                        onChange={(v) => setFilterTier(v)}
                        allowClear
                    >
                        <Select.Option value="S">Tier S</Select.Option>
                        <Select.Option value="A">Tier A</Select.Option>
                        <Select.Option value="B">Tier B</Select.Option>
                        <Select.Option value="C">Tier C</Select.Option>
                    </Select>
                    <Select
                        placeholder="Estado"
                        style={{ width: 130 }}
                        value={filterActive}
                        onChange={(v) => setFilterActive(v)}
                        allowClear
                    >
                        <Select.Option value={1}>Activos</Select.Option>
                        <Select.Option value={0}>Inactivos</Select.Option>
                    </Select>
                    <Button onClick={() => {
                        setFilterName('');
                        setFilterSeason(null);
                        setFilterRarity(null);
                        setFilterTier(null);
                        setFilterActive(null);
                    }}>Limpiar Filtros</Button>
                </Space>
            </div>

            {/* Botones de acción y selector de vista */}
            <Space style={{ marginBottom: 16 }} wrap>
                {isAdmin && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setJavamonModal(true); setFileList([]); setPreviewImage(null); }}>
                        Crear Javamon
                    </Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={fetchConfig} loading={loading}>Refrescar</Button>
                <Select
                    value={javamonView}
                    onChange={(v) => setJavamonView(v)}
                    style={{ width: 240 }}
                    options={[
                        { value: 'table', label: '📋 Vista Tabla' },
                        { value: 'cards', label: '🖼️ Vista Cuadrícula' },
                        { value: 'grid', label: '🧬 Vista Evoluciones' },
                        { value: 'seasons', label: '📅 Vista Temporadas' },
                        { value: 'usage', label: '🎁 Vista de Uso / Recompensas' }
                    ]}
                />
            </Space>

            {/* Vista seleccionada */}
            {javamonView === 'table' && (
                <Table
                    dataSource={getFilteredJavamons()}
                    columns={columnsJavamon}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1350 }}
                    size="small"
                    pagination={{ pageSize: 15, showSizeChanger: true }}
                />
            )}
            {javamonView === 'cards' && renderJavamonCardsGrid()}
            {javamonView === 'grid' && renderJavamonGrid()}
            {javamonView === 'seasons' && renderSeasonsGrid()}
            {javamonView === 'usage' && (
                <RewardsUsageView
                    type="javamon"
                    items={getFilteredJavamons()}
                    rewardsUsage={rewardsUsage}
                    loading={loading}
                    loadingRewardsUsage={loadingRewardsUsage}
                    fetchRewardsUsage={fetchRewardsUsage}
                />
            )}

            {/* Modal Javamon */}
            <Modal title={editingItem ? "Editar Javamon" : "Crear Javamon"} open={javamonModal} onCancel={() => setJavamonModal(false)} onOk={() => form.submit()} destroyOnClose width={700}>
                <Form form={form} layout="vertical" onFinish={handleSaveJavamon} initialValues={{ active: 1, tier: 'C' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item name="nom" label="Nombre" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="tier" label="Tier">
                            <Select>
                                <Select.Option value="S">Tier S</Select.Option>
                                <Select.Option value="A">Tier A</Select.Option>
                                <Select.Option value="B">Tier B</Select.Option>
                                <Select.Option value="C">Tier C</Select.Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item name="id_rarity" label="Rareza" rules={[{ required: true }]}>
                            <Select>
                                {config.rarities.map((r: any) => (
                                    <Select.Option key={r.id} value={r.id}>
                                        <Tag color={r.color}>{r.nom}</Tag>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="id_season" label="Temporada" rules={[{ required: true }]}>
                            <Select>
                                {config.seasons.map((s: any) => <Select.Option key={s.id} value={s.id}>{s.nom}</Select.Option>)}
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item name="chance" label="% de Aparición en esta Rareza" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
                        <Form.Item name="vida" label="Vida">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="defensa" label="Defensa">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="velocidad" label="Velocidad">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="ataque" label="Ataque">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </div>

                    <Form.Item name="image" label="Ruta de Imagen (o subir nueva)">
                        <Input disabled={fileList.length > 0} placeholder="/TEMP1/normal/JM001.png" />
                    </Form.Item>

                    <Form.Item label="Subir nueva imagen (reemplazará la actual)">
                        <Upload
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={async ({ fileList }) => {
                                setFileList(fileList);
                                if (fileList.length > 0 && fileList[0].originFileObj) {
                                    const b64 = await getBase64(fileList[0].originFileObj);
                                    setPreviewImage(b64);
                                    if (!editingItem && !form.getFieldValue('nom')) {
                                        const fileName = fileList[0].name || '';
                                        const nameFromFile = fileName.replace(/\.[^/.]+$/, '');
                                        if (nameFromFile) {
                                            form.setFieldsValue({ nom: nameFromFile });
                                        }
                                    }
                                } else {
                                    setPreviewImage(null);
                                }
                            }}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
                        </Upload>
                        {(previewImage || editingItem) && (
                            <div style={{ marginTop: 10 }}>
                                <Image src={previewImage || javamonImage(editingItem)} alt="preview" width={100} style={{ borderRadius: 8, border: '1px solid #d9d9d9', padding: 4 }} />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item name="descr" label="Descripción">
                        <Input.TextArea rows={3} placeholder="Pista de problema: JM001, +10% nota..." />
                    </Form.Item>

                    <Form.Item
                        name="habilitat_ids"
                        label="Habilidades (magias que lanza en combate)"
                        tooltip="El JAVAmon alternará aleatoriamente entre estas magias para atacar al jugador en el battleground."
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Selecciona una o varias magias..."
                            optionFilterProp="label"
                            options={(skinsConfig.magias || []).map((m: any) => ({
                                value: m.id,
                                label: `${m.nom}${m.tipo ? ` (${m.tipo})` : ''}`,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="evoluciona_a"
                        label="Evoluciona a"
                        tooltip="Si se indica, este Javamon se transformará automáticamente en el seleccionado al evolucionar."
                    >
                        <Select
                            allowClear
                            showSearch
                            placeholder="No evoluciona"
                            optionFilterProp="label"
                            options={config.javamons
                                .filter((j: any) => !editingItem || j.id !== editingItem.id)
                                .map((j: any) => ({ value: j.id, label: j.nom }))}
                        />
                    </Form.Item>

                    <Form.Item name="active" label="Aparece en el juego" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Temporada */}
            <Modal title={editingItem ? "Editar Temporada" : "Nueva Temporada"} open={seasonModal} onCancel={() => setSeasonModal(false)} onOk={() => form.submit()} destroyOnClose>
                <Form form={form} layout="vertical" onFinish={handleSaveSeason} initialValues={{ active: 1 }}>
                    <Form.Item name="nom" label="Nombre de la Temporada" rules={[{ required: true }]}>
                        <Input placeholder="Ej: Temporada 1, Especial Navidad..." />
                    </Form.Item>
                    <Form.Item name="descr" label="Descripción">
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item name="active" label="Activa" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Rareza */}
            <Modal title={editingItem ? "Editar Rareza" : "Nueva Rareza"} open={rarityModal} onCancel={() => setRarityModal(false)} onOk={() => form.submit()} destroyOnClose>
                <Form form={form} layout="vertical" onFinish={handleSaveRarity} initialValues={{ active: 1, chance: 0 }}>
                    <Form.Item name="nom" label="Nombre" rules={[{ required: true }]}>
                        <Input placeholder="Ej: Legendario, Épico..." />
                    </Form.Item>
                    <Form.Item name="color" label="Color Asignado" rules={[{ required: true }]}>
                        <ColorPicker showText />
                    </Form.Item>
                    <Form.Item name="chance" label="% de Aparición" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="active" label="Activa" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Gestión de Rarezas */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>💎</span>
                        <span>Rarezas y Probabilidades Globales</span>
                    </div>
                }
                open={rarityManageModal}
                onCancel={() => setRarityManageModal(false)}
                footer={[
                    <Button key="close" onClick={() => setRarityManageModal(false)}>
                        Cerrar
                    </Button>
                ]}
                width={720}
                destroyOnClose
            >
                <div style={{
                    marginBottom: 16,
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 10
                }}>
                    <div>
                        <b>Probabilidad Total: {config.rarities.reduce((acc: number, r: any) => acc + (r.chance || 0), 0)}%</b>
                        <span style={{ marginLeft: 8, color: '#64748b', fontSize: 13 }}>(Debe sumar exactamente 100%)</span>
                    </div>
                    {config.rarities.reduce((acc: number, r: any) => acc + (r.chance || 0), 0) === 100 ? (
                        <Tag color="success">✓ Suma 100%</Tag>
                    ) : (
                        <Tag color="warning">⚠️ No suma 100%</Tag>
                    )}
                </div>
                <Space style={{ marginBottom: 16 }}>
                    {isAdmin && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => { setEditingItem(null); form.resetFields(); setRarityModal(true); }}
                        >
                            Nueva Rareza
                        </Button>
                    )}
                    <Button icon={<ReloadOutlined />} onClick={fetchConfig} loading={loading}>
                        Refrescar
                    </Button>
                </Space>
                <Table
                    dataSource={[...config.rarities].sort((a: any, b: any) => (b.chance || 0) - (a.chance || 0))}
                    columns={columnsRarity}
                    loading={loading}
                    rowKey="id"
                    pagination={false}
                    size="small"
                />
            </Modal>
        </div>
    );
};

export default TabJavamons;
